/**
 * KRO Internal Platform Admin Dashboard
 *
 * Route: /admin  (requires isPlatformAdmin=true on /users/{uid})
 * Shows global platform metrics across all tenant companies.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  DollarSign, Building2, Receipt, TrendingUp, ShieldAlert,
  RefreshCw, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PlatformActivity from '../components/admin/PlatformActivity';

const API = process.env.REACT_APP_BACKEND_URL;

async function getAuthToken() {
  const { getAuth } = await import('firebase/auth');
  const u = getAuth().currentUser;
  if (!u) throw new Error('Not authenticated');
  return u.getIdToken();
}

const fmt = (n) =>
  Number(n).toLocaleString('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  });

const fmtPrecise = (n) =>
  Number(n).toLocaleString('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2,
  });

// ── Summary card ─────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, icon: Icon, iconBg, iconColor, testId }) => (
  <div
    className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm
      hover:shadow-md hover:border-indigo-200 transition-all duration-200"
    data-testid={testId}
  >
    <div className="flex items-start justify-between mb-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</p>
      {Icon && (
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
      )}
    </div>
    <p className="text-2xl font-bold text-gray-900 leading-none" data-testid={`${testId}-value`}>
      {value}
    </p>
    {sub && <p className="text-xs text-gray-400 mt-1.5">{sub}</p>}
  </div>
);

// ── Stripe connect status pill ────────────────────────────────────────────────

const StripeStatusPill = ({ status }) => {
  const styles = {
    active:        'bg-green-50  text-green-700  border border-green-200',
    pending:       'bg-amber-50  text-amber-700  border border-amber-200',
    not_connected: 'bg-gray-100  text-gray-500   border border-gray-200',
  };
  const labels = {
    active:        'Connected',
    pending:       'Pending',
    not_connected: 'Not Connected',
  };
  const cls = styles[status] || styles.not_connected;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status === 'active' && <CheckCircle2 className="w-3 h-3" />}
      {status !== 'active' && <AlertCircle  className="w-3 h-3" />}
      {labels[status] || status}
    </span>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────

const AdminDashboard = () => {
  const { user, profile, isAuthLoading } = useAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // ── Access guard ─────────────────────────────────────────────────────────
  // Wait for auth + profile to resolve before rendering anything.
  // Once resolved: non-platform-admins are hard-redirected to /dashboard.
  if (isAuthLoading || (user && !profile)) {
    // Still hydrating — render nothing so the redirect decision is accurate.
    return null;
  }
  if (!user || !profile?.isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  // ─────────────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const res = await axios.get(`${API}/api/admin/platform-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load platform stats';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <ShieldAlert className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" data-testid="admin-dashboard-title">
                Platform Admin
              </h1>
              <p className="text-xs text-gray-500">KRO internal — all-time metrics</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="admin-refresh-btn"
            className="gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error state */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2
            text-red-700 text-sm" data-testid="admin-error">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-8"
          data-testid="admin-kpi-grid">

          <StatCard
            label="Total Volume"
            value={loading ? '—' : fmt(data?.totalVolume ?? 0)}
            sub="all paid invoices"
            icon={DollarSign}
            iconBg="bg-emerald-50"
            iconColor="text-emerald-600"
            testId="admin-kpi-volume"
          />
          <StatCard
            label="Platform Fees"
            value={loading ? '—' : fmtPrecise(data?.totalPlatformFees ?? 0)}
            sub="KRO earnings"
            icon={TrendingUp}
            iconBg="bg-indigo-50"
            iconColor="text-indigo-600"
            testId="admin-kpi-fees"
          />
          <StatCard
            label="Paid Invoices"
            value={loading ? '—' : (data?.totalPaidInvoices ?? 0).toLocaleString()}
            sub="all-time across all companies"
            icon={Receipt}
            iconBg="bg-orange-50"
            iconColor="text-orange-600"
            testId="admin-kpi-invoices"
          />
          <StatCard
            label="Total Companies"
            value={loading ? '—' : (data?.totalCompanies ?? 0).toLocaleString()}
            sub="registered tenants"
            icon={Building2}
            iconBg="bg-slate-100"
            iconColor="text-slate-600"
            testId="admin-kpi-companies"
          />
          <StatCard
            label="Active Companies"
            value={loading ? '—' : (data?.activeCompanies ?? 0).toLocaleString()}
            sub="with at least 1 paid invoice"
            icon={CheckCircle2}
            iconBg="bg-green-50"
            iconColor="text-green-600"
            testId="admin-kpi-active"
          />
        </div>

        {/* Sprint 247 — Platform Activity (read-only operational view) */}
        <PlatformActivity />

        {/* Company Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
          data-testid="admin-company-table">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-700">Company Breakdown</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Sorted by volume — all-time paid invoices
            </p>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-5 h-5 animate-spin text-gray-300 mx-auto" />
              <p className="text-sm text-gray-400 mt-2">Loading…</p>
            </div>
          ) : !data?.companies?.length ? (
            <div className="p-8 text-center text-sm text-gray-400">No companies found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Paid Invoices
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Volume
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Platform Fee
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Stripe Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.companies.map((co) => (
                    <tr
                      key={co.id}
                      className="hover:bg-gray-50 transition-colors"
                      data-testid={`admin-company-row-${co.id}`}
                    >
                      <td className="px-5 py-3.5">
                        <div className="font-medium text-gray-900 truncate max-w-[200px]">
                          {co.name}
                        </div>
                        <div className="text-xs text-gray-400 font-mono truncate max-w-[200px]">
                          {co.id}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-right text-gray-700 tabular-nums">
                        {co.paidInvoiceCount}
                      </td>
                      <td className="px-4 py-3.5 text-right font-semibold text-gray-900 tabular-nums">
                        {co.volume > 0 ? fmtPrecise(co.volume) : (
                          <span className="text-gray-400 font-normal">$0.00</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-right tabular-nums">
                        {co.platformFees > 0 ? (
                          <span className="text-indigo-700 font-semibold">
                            {fmtPrecise(co.platformFees)}
                          </span>
                        ) : (
                          <span className="text-gray-400">$0.00</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <StripeStatusPill status={co.stripeConnectStatus} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Footer totals */}
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                    <td className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Totals
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900 tabular-nums">
                      {data.totalPaidInvoices}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900 tabular-nums">
                      {fmtPrecise(data.totalVolume)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-indigo-700 tabular-nums">
                      {fmtPrecise(data.totalPlatformFees)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Footer note */}
        <p className="text-xs text-gray-400 mt-4 text-center">
          Platform fee rate is currently set to 0% (infrastructure live, monetization off).
          Update <code className="font-mono bg-gray-100 px-1 py-0.5 rounded">KRO_PLATFORM_FEE_RATE</code> in backend .env to activate.
        </p>
      </div>
    </div>
  );
};

export default AdminDashboard;
